/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CancelOutlined,
  SaveOutlined as SaveIcon,
  ArrowCircleUpOutlined,
  SettingsOutlined,
  InfoOutlined,
} from '@mui/icons-material/';
import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded';
import { Box, IconButton, Typography, styled, Divider, Stack, Modal, Paper, Button } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { Toast } from 'app/components/';
import { RequestDatatype, BoardColumn, Alert } from 'app/components/v2';
import EditDataClassModal from 'app/components/v2/EditDataClassModal';
import {
  dragDropItems,
  repositionColumns,
  setChanges,
  setPrivacyPersistedError,
  setWithoutTemp,
} from 'app/store/features/privacyPersistedSlice';
import { deletePolicyByCache, savePolicy, setPrivacyError } from 'app/store/features/privacySlice';
import { ColorsV2 } from 'app/constants/color';
import { routes } from 'app/routes';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Buttons } from 'app/constants/sizing';
import InfoObjectModal from './InfoObjectModal';
import { checkForAssignedChartColor } from '../../utils';

function Board({ beingEdited }) {
  const Title = styled(Typography)({
    fontFamily: 'DM Sans,sans-serif',
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '31.25px',
    letterSpacing: '0.1px',
    color: '#526380',
    padding: '8px',
  });

  const withoutTemplate = useSelector(state => state.privacyPersisted.WithoutTemplate);
  const privacyPersistedError = useSelector(state => state.privacyPersisted.privacyPersistedError);
  const changes = useSelector(state => state.privacyPersisted.changes);
  const privacyError = useSelector(state => state.privacy.privacyError);

  const chartColors = useSelector(state => state?.userSettings.settings.chartColors) || {};

  const getColor = id => {
    return chartColors[id] || checkForAssignedChartColor(id);
  };

  const formatData = data => {
    const result = {};
    const sortedData = data ? [...data]?.sort((a, b) => a.sortOrder - b.sortOrder) : [];
    sortedData?.forEach(({ id, name, description, sortOrder, infoObjects = [], protectionAspects = [], ...additionalProperties }) => {
      result[id] = {
        name, // Main properties included here for clarity even if they do not require renaming
        describe: description,
        sortOrder,
        items: infoObjects,
        protectionAspects,
        id,
        ...additionalProperties, // Include any additional properties
      };
    });

    return result;
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation('translation', { keyPrefix: 'policy_manager' });

  const action = location.state?.action;
  const isViewPage = action === 'VIEW' || action === 'VIEW POLICY';

  const [showToast, setShowToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [columns, setColumns] = useState({});

  const [infoObjectModal, setInfoObjectModal] = useState({open: false, color: null, id: null});
  const [isOpen, setIsOpen] = useState({
    requestDatatype: false,
    addColumn: false,
    privacyModalOpen: false,
    confirmationModel: false,
  });

  useEffect(() => {
    const newColumns = formatData(withoutTemplate?.Data?.levels);
    Object.keys(newColumns).forEach((key) => {
      newColumns[key].color = getColor(key); // Assign color dynamically
    });
    setColumns(newColumns);
  }, [withoutTemplate]);

  useEffect(() => {
    if (privacyError) {
      setShowToast({
        open: true,
        message: privacyError.hint || 'Something went Wrong',
        severity: 'error',
      });
    }

    if (privacyPersistedError) {
      setShowToast({
        open: true,
        message: privacyPersistedError.hint || 'Something went Wrong',
        severity: 'error',
      });
    }
  }, [privacyError, privacyPersistedError]);

  const resetToast = () => {
    setShowToast({
      open: false,
      message: '',
      severity: 'success',
    });
  };

  const handleCloseToast = () => {
    dispatch(setPrivacyError(null));
    dispatch(setPrivacyPersistedError(null));
    resetToast();
  };

  const handleSave = async () => {
    if (beingEdited) {
      setShowToast({
        open: true,
        message: t('policy_name_error_first'),
        severity: 'error',
      });
      return;
    }
    dispatch(savePolicy(withoutTemplate?.policyType)).then(res => {
      if (!res.error) {
        navigate(routes.PRIVACY);
      }
    });
  };

  const onDragEnd = result => {
    if (!result.destination || action === 'VIEW' || action === 'VIEW POLICY') return;
    const { source, destination, type, draggableId } = result;

    if (type === 'column') {
      if (destination.index === 0) return;
      const updatedColumns = { ...columns };
      const columnOrder = Object.keys(updatedColumns);
      const [removed] = columnOrder.splice(source.index, 1);
      columnOrder.splice(destination.index, 0, removed);

      const updatedColumnsObj = columnOrder.reduce((acc, columnId) => {
        acc[columnId] = updatedColumns[columnId];
        return acc;
      }, {});
      const temp = columns;
      setColumns(updatedColumnsObj);
      dispatch(repositionColumns({ id: removed, obj: { sortOrder: destination.index } })).then(
        res => {
          if (res.error) {
            setColumns(temp);
          }
        }
      );
    } else {
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];

      if (sourceColumn === destColumn) {
        const items = [...sourceColumn.items];
        const [removed] = items.splice(source.index, 1);
        items.splice(destination.index, 0, removed);

        const updatedColumns = {
          ...columns,
          [source.droppableId]: {
            ...sourceColumn,
            items,
          },
        };

        const temp = columns;
        setColumns(updatedColumns);
        dispatch(
          dragDropItems({ id: destColumn?.id, dragId: draggableId, index: destination.index })
        ).then(res => {
          if (res.error) {
            setColumns(temp);
          }
        });
      } else {
        const sourceItems = [...sourceColumn.items];
        const destItems = [...destColumn.items];
        const [removed] = sourceItems.splice(source.index, 1);
        destItems.splice(destination.index, 0, removed);

        const updatedColumns = {
          ...columns,
          [source.droppableId]: {
            ...sourceColumn,
            items: sourceItems,
          },
          [destination.droppableId]: {
            ...destColumn,
            items: destItems,
          },
        };
        const temp = columns;
        setColumns(updatedColumns);
        dispatch(
          dragDropItems({ id: destColumn?.id, dragId: draggableId, index: destination.index })
        ).then(res => {
          if (res.error) {
            setColumns(temp);
            dispatch(setWithoutTemp(columns));
          }
        });
      }
    }
  };

  const [columnData, setColumnData] = useState({
    name: '',
    title: '',
    describe: '',
    id: '',
  });

  const togglePopup = () => {
    setIsOpen({
      ...isOpen,
      requestDatatype: true,
    });
  };

  const handleAdd = () => {
    setIsOpen({
      ...isOpen,
      addColumn: true,
    });
  };

  const cancelConfirmation = () => {
    dispatch(deletePolicyByCache(withoutTemplate?.policyType)).then(res => {
      if (!res.error) {
        dispatch(setChanges(false));
        navigate(routes.PRIVACY);
      }
    });
  };
  const handleCancel = () => {
    if (changes) {
      setIsOpen({ ...isOpen, confirmationModel: true });
    } else {
      cancelConfirmation();
    }
  };

  return (
    <div
      style={{
        overflow: 'scroll',
      }}
    >
      <Toast
        open={showToast.open}
        setOpen={handleCloseToast}
        message={showToast.message}
        severity={showToast.severity}
        isLocked={false}
      />
        <Divider sx={{ bgcolor: ColorsV2.white, mb: '10px' }} />  
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
        }}
      >
        {/* <Typography variant='h4' sx={{ color: ColorsV2.white, marginBottom: '10px' }}>{t('kanban_board_header')}</Typography> */}
        {action !== 'VIEW' && action !== 'VIEW POLICY' ? (
          <Stack direction='column' gap={1} sx={{marginY: '10px'}}>
            <Box sx={{ gap: '10px', display: 'flex', flexDirection: 'row' }}>
              <Button
                name={'IconButton'}
                startIcon={<CancelOutlined sx={{ color: ColorsV2.white, fontSize: '0.7em' }} />}
                onClick={handleCancel}
                sx={{ ...Buttons.baseStyle.primaryButtonLowPriority, color: ColorsV2.white, minWidth: '100px' }}
              >
                {t('cancel')}
              </Button>
              <Button
                name={'IconButton'}
                onClick={handleSave}
                startIcon={<SaveIcon sx={{ color: ColorsV2.white, fontSize: '0.7em' }} />}
                sx={{ ...Buttons.baseStyle.primaryButtonLowPriority, color: ColorsV2.white, minWidth: '100px' }}
              >
                {t('save')}
              </Button>

              <Button
                name={'IconButton'}
                startIcon={<ControlPointRoundedIcon sx={{ color: ColorsV2.white }} />}
                onClick={handleAdd}
                sx={{ ...Buttons.baseStyle.primaryButtonLowPriority, color: ColorsV2.white, minWidth: '100px' }}
              >
                {t('new_class')}
              </Button>
            </Box>
            <Button
                name={'IconButton'}
                onClick={togglePopup}
                startIcon={
                  <ArrowCircleUpOutlined sx={{ color: ColorsV2.white, fontSize: '0.7em' }} />
                }
                id='step-8'
                sx={{ ...Buttons.baseStyle.primaryButtonLowPriority, color: ColorsV2.white, minWidth: '100px' }}
              >
                {t('request_datatype')}
              </Button>
          </Stack>
        ) : (
          ''
        )}
      </Box>
      <div style={{ display: 'flex', overflow: 'scroll', position: 'relative', backgroundColor: ColorsV2.primary, borderRadius: '4px', padding: '10px'}}>
        <DragDropContext onDragEnd={result => onDragEnd(result)}>
          <Droppable droppableId='all-columns' direction='horizontal' type='column' sx={{ maxHeight: '100%' }}>
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef} style={{ display: 'flex' }}>
                {Object.entries(columns).map(([columnId, column], columnIndex) => (
                  <Draggable key={columnId} draggableId={columnId} index={columnIndex}>
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          ...provided.draggableProps.style,
                        }}
                      >
                        <div style={{ margin: 8 }} {...provided.dragHandleProps}>
                          <Droppable droppableId={columnId} key={columnId} type='item'>
                            {provided => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{
                                  background: ColorsV2.secondary,
                                  border: `2px solid ${ColorsV2.outline}`,
                                  padding: '10px 10px 10px 10px',
                                  width: '15vw',
                                  borderRadius: '4px',
                                  height: '550px'
                                }}
                              >
                                <div
                                  style={{
                                    // borderRadius: '25px',
                                    background: 'transparent',
                                    width: '100%',
                                    marginBottom: '10px',
                                    paddingBottom: '4px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '5px',
                                    color: ColorsV2.white,
                                    borderBottom: '10px solid ' + column?.color,
                                    borderRadius: '0px 0px 4px 4px',
                                  }}
                                >
                                  <Stack direction='row' alignItems='center'>
                                    <p
                                      style={{
                                        margin: '0',
                                        fontFamily: 'DM Sans,sans-serif',
                                        fontWeight: 'bold',
                                        fontSize: '0.8em',
                                      }}
                                    >
                                      {column?.name}
                                    </p>
                                    <p
                                      style={{
                                        margin: '0',
                                        fontFamily: 'DM Sans,sans-serif',
                                        fontSize: '0.8em',
                                      }}
                                    >
                                      {column?.title}
                                    </p>
                                    {column?.describe && (
                                      <Tooltip
                                        title={column.describe}
                                        sx={{
                                          '& .MuiTooltip-tooltip': {
                                            fontSize: '0.4em',
                                            fontWeight: 'light',
                                          },
                                        }}
                                      >
                                        <IconButton sx={{
                                        width: '2.625rem',
                                        height: '2.625rem',
                                        color: ColorsV2.textInfo,
                                        }}>
                                          <InfoOutlined />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  <div>
                                    {!!columnIndex && !isViewPage && (
                                      <IconButton
                                        sx={{ ...Buttons.baseStyle.iconButton, color: ColorsV2.textInfo }}
                                        onClick={() => {
                                          setIsOpen({
                                            ...isOpen,
                                            privacyModalOpen: true,
                                          });
                                          setColumnData({
                                            ...column,
                                            id: columnId,
                                          });
                                        }}
                                      >
                                        <SettingsOutlined />
                                      </IconButton>
                                    )}
                                  </div>
                                </div>
                                <div
                                  style={{
                                    height: '430px',
                                    maxHeight: '430px',
                                    overflowY: 'scroll',
                                    overflowX: 'hidden',
                                    border: `2px solid ${ColorsV2.outline}`,
                                    padding: '8px',
                                    background: ColorsV2.primary,
                                    borderRadius: '4px'
                                  }}
                                >
                                  {column?.items?.map((item, itemIndex) => {
                                    return (
                                      <Draggable
                                        key={item?.id}
                                        draggableId={item?.id}
                                        index={itemIndex}
                                      >
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                              userSelect: 'none',
                                              padding: 8,
                                              margin: '0 0 8px 0',
                                              fontFamily: 'DM Sans,sans-serif',
                                              background: ColorsV2.secondary,
                                              borderTop: '4px solid ' + getColor(item?.id),
                                              borderRadius: '4px 4px 0px 0px',
                                              // color: new TinyColor(getColor(item?.id)).isLight() ? "#000000" : "#FFFFFF",
                                              color: ColorsV2.textInfo,
                                              fontSize: '0.7em',
                                              fontWeight: 600,
                                              ...provided.draggableProps.style,
                                            }}
                                          >
                                            <Stack direction='row' alignItems='center' justifyContent='space-between'>
                                              <Typography color={ColorsV2.white} variant='body2'>{item?.name}</Typography>
                                              
                                              {!isViewPage && <IconButton
                                              onClick={() => setInfoObjectModal((prev) => ({
                                                ...prev,
                                                color: getColor(item?.id), 
                                                id: item?.id,
                                                open: prev.open ? false : true,
                                                }))}
                                              sx={{...Buttons.baseStyle.iconButton, color: ColorsV2.textInfo, }}
                                              >
                                                <SettingsOutlined sx={{ width: '1.25rem', height: '1.25rem' }}></SettingsOutlined>
                                              </IconButton>}
                                            </Stack>
                                            <Typography color={ColorsV2.textInfo} variant='body2'>
                                              {item?.description}
                                            </Typography>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                </div>
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {isOpen.privacyModalOpen && (
          <EditDataClassModal
            isOpen={isOpen}
            columnData={columnData}
            setColumnData={setColumnData}
            setIsOpen={setIsOpen}
            columns={columns}
            setColumns={setColumns}
          />
        )}
        <InfoObjectModal
        color={infoObjectModal.color}
        id={infoObjectModal.id}
        open={infoObjectModal.open}
        onClose={() => setInfoObjectModal((prev) => ({...prev, open: false, id: null, color: null}))}
        t={t}
        ></InfoObjectModal>
        {/* {showCalender && (
          <CalenderContainer>
            <Calender />
          </CalenderContainer>
        )} */}
        {isOpen.requestDatatype && <RequestDatatype setIsOpen={setIsOpen} isOpen={isOpen} />}
      </div>
      {/* <Tour /> */}
      {isOpen.addColumn && <BoardColumn setIsOpen={setIsOpen} isOpen={isOpen} />}
      <Alert
        open={isOpen.confirmationModel}
        title={t('kanban_alert_exit')}
        text={t('kanban_alert_exit_description')}
        setOpen={setIsOpen}
        handleConfirmation={cancelConfirmation}
      />
    </div>
  );
}

export default Board;
